import { ArrowLeftIcon, CopyIcon, TrashIcon } from '@radix-ui/react-icons';
import {
  Badge,
  Box,
  Button,
  Code,
  Flex,
  Heading,
  IconButton,
  Spinner,
  Tabs,
  Text,
} from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonCallout } from 'components/common/callouts';
import { CommonConfirmationDialog } from 'components/common/dialogs/confirmation';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonCheckboxInput } from 'components/common/form/checkbox';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonTextInput } from 'components/common/form/text';
import { FeatureFlagAuditHistory } from 'components/platform-console/sections/feature-flags/audit-history';
import {
  FeatureFlagCriteriaComposer,
  IFeatureFlagCriteriaState,
} from 'components/platform-console/sections/feature-flags/criteria-composer';
import { FeatureFlagDuplicateDialog } from 'components/platform-console/sections/feature-flags/duplicate-dialog';
import env from 'config';
import { t } from 'i18next';
import { FeatureFlagHelper } from 'lib_ts/classes/feature-flag.helper';
import {
  FEATURE_FLAG_ENVIRONMENT_OPTIONS,
  FeatureFlagCriterionType,
  FeatureFlagEnvironment,
  FeatureFlagMachineMode,
} from 'lib_ts/enums/feature-flags.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import {
  CONTINUOUS_TRAINING_SUPPORTED_DEVICES,
  FF_NAME_CONTINUOUS_TRAINING,
} from 'lib_ts/enums/training.enums';
import {
  IFeatureFlag,
  IFeatureFlagCriterion,
} from 'lib_ts/interfaces/feature-flags/i-feature-flag';
import {
  ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { paths } from 'routes/paths';
import { PlatformConsoleService } from 'services/platform-console.service';
import { useAuthStore } from 'stores/use-auth-store';

const COMPONENT_NAME = 'FeatureFlagDetailPage';

const DEFAULT_ENVIRONMENT =
  FeatureFlagHelper.mapRuntimeToEnvironment(env.identifier) ??
  FeatureFlagEnvironment.Dev;

/** environments this deployment is allowed to manage (DB-access scoped) */
const MANAGEABLE_ENVIRONMENTS = FeatureFlagHelper.getManageableEnvironments(
  env.identifier
);

/** duplicating needs at least one manageable environment other than the source */
const CAN_DUPLICATE = MANAGEABLE_ENVIRONMENTS.length > 1;

type DetailTab = 'details' | 'history';

interface IProps {
  mode: 'create' | 'edit';
}

const environmentLabel = (environment: FeatureFlagEnvironment): string =>
  FEATURE_FLAG_ENVIRONMENT_OPTIONS.find((o) => o.value === environment)
    ?.label ?? environment;

/**
 * Builds a representative evaluation context from the given targeting criteria
 * (one value per configured dimension), so the backend snippet can be copied
 * and produce a truthy evaluation. No criteria means a simple on/off toggle
 * whose context is ignored.
 */
const buildContextLines = (criteria: IFeatureFlagCriterion[]): string[] => {
  const contextLines: string[] = [];

  for (const criterion of criteria) {
    switch (criterion.type) {
      case FeatureFlagCriterionType.Role:
        if (criterion.roles.length > 0) {
          contextLines.push(`  role: '${criterion.roles[0]}',`);
        }
        break;

      case FeatureFlagCriterionType.User:
        if (criterion.userIds.length > 0) {
          contextLines.push(`  userID: '${criterion.userIds[0]}',`);
        }
        break;

      case FeatureFlagCriterionType.Team:
        if (criterion.teamIds.length > 0) {
          contextLines.push(`  teamID: '${criterion.teamIds[0]}',`);
        }
        break;

      case FeatureFlagCriterionType.Machine: {
        if (criterion.mode === FeatureFlagMachineMode.All) {
          contextLines.push(`  machineID: '<any-machine-id>',`);
        } else {
          const ids = criterion.machineIds ?? [];
          if (ids.length > 0) {
            contextLines.push(`  machineID: '${ids[0]}',`);
          }
        }
        break;
      }
    }
  }

  return contextLines;
};

/**
 * Server-side evaluation snippet (`FeatureFlagService.isEnabled`), usable from
 * controllers, services, and jobs. The context mirrors the flag's targeting so
 * the copied call evaluates truthy.
 */
const buildBackendSnippet = (
  name: string,
  criteria: IFeatureFlagCriterion[]
): string => {
  const contextLines = buildContextLines(criteria);

  const header =
    contextLines.length > 0
      ? "// server-side (controller / service / job) — context matches this flag's targeting"
      : '// server-side (controller / service / job) — simple on/off flag, context is ignored';

  const context =
    contextLines.length > 0 ? `{\n${contextLines.join('\n')}\n}` : '{}';

  return `${header}\nconst enabled = await FeatureFlagService.isEnabled('${name}', ${context});`;
};

/**
 * Client-side evaluation snippet (`useFeatureFlag` hook). The frontend reads a
 * server-evaluated boolean map fetched once post-auth, so the context/targeting
 * is applied on the server and the component just reads the result.
 */
const buildFrontendSnippet = (name: string): string =>
  [
    '// client-side (React component) — reads the server-evaluated map from login',
    '// targeting criteria are gathered server-side from your auth token, so no',
    '// context is passed here (and the map refreshes on next reload)',
    `const enabled = useFeatureFlag('${name}');`,
  ].join('\n');

type TsTokenType = 'comment' | 'keyword' | 'string' | 'fn' | 'key' | 'plain';

/** Radix step-11 scales read as accessible text in both light & dark modes */
const TS_TOKEN_COLOR: Record<TsTokenType, string | undefined> = {
  comment: 'var(--gray-a10)',
  keyword: 'var(--purple-11)',
  string: 'var(--grass-11)',
  fn: 'var(--blue-11)',
  key: 'var(--amber-11)',
  plain: undefined,
};

const TS_KEYWORDS = new Set([
  'const',
  'let',
  'var',
  'await',
  'async',
  'return',
  'function',
  'import',
  'from',
  'new',
]);

const TS_TOKEN_RE = /(\/\/[^\n]*)|('(?:[^'\\]|\\.)*')|([A-Za-z_$][\w$]*)/g;

/**
 * Minimal TS tokenizer for the short, fully-controlled usage snippets — avoids
 * pulling in a syntax-highlighting dependency. Classifies comments, strings,
 * keywords, call expressions (`ident(`), and object keys (`ident:`); everything
 * else stays plain.
 */
const tokenizeTs = (code: string): { text: string; type: TsTokenType }[] => {
  const tokens: { text: string; type: TsTokenType }[] = [];
  let last = 0;
  let match: RegExpExecArray | null;

  TS_TOKEN_RE.lastIndex = 0;

  while ((match = TS_TOKEN_RE.exec(code)) !== null) {
    if (match.index > last) {
      tokens.push({ text: code.slice(last, match.index), type: 'plain' });
    }

    const [full, comment, str, word] = match;

    if (comment) {
      tokens.push({ text: full, type: 'comment' });
    } else if (str) {
      tokens.push({ text: full, type: 'string' });
    } else if (TS_KEYWORDS.has(word)) {
      tokens.push({ text: full, type: 'keyword' });
    } else {
      const nextChar = code.slice(match.index + full.length).trimStart()[0];
      tokens.push({
        text: full,
        type: nextChar === '(' ? 'fn' : nextChar === ':' ? 'key' : 'plain',
      });
    }

    last = match.index + full.length;
  }

  if (last < code.length) {
    tokens.push({ text: code.slice(last), type: 'plain' });
  }

  return tokens;
};

const HighlightedTs = (props: { code: string }) => (
  <>
    {tokenizeTs(props.code).map((token, i) => (
      <span
        key={i}
        style={{
          color: TS_TOKEN_COLOR[token.type],
          fontStyle: token.type === 'comment' ? 'italic' : undefined,
        }}
      >
        {token.text}
      </span>
    ))}
  </>
);

/** labelled, copyable code box used for each usage example */
const UsageSnippet = (props: {
  label: string;
  color: ComponentProps<typeof Badge>['color'];
  snippet: string;
}) => (
  <Flex direction="column" gap="2">
    <Badge
      color={props.color}
      variant="soft"
      radius="full"
      style={{
        width: 'fit-content',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        fontWeight: 600,
      }}
    >
      {props.label}
    </Badge>
    <Box style={{ position: 'relative', width: '100%' }}>
      <Box
        style={{
          backgroundColor: 'var(--gray-a2)',
          border: '1px solid var(--gray-a5)',
          borderRadius: 'var(--radius-3)',
          padding: 'var(--space-3)',
          paddingRight: 'var(--space-8)',
          overflowX: 'auto',
        }}
      >
        <pre
          style={{
            margin: 0,
            fontFamily: 'var(--code-font-family)',
            fontSize: 'var(--font-size-2)',
            lineHeight: 'var(--line-height-2)',
            whiteSpace: 'pre',
            color: 'var(--gray-12)',
          }}
        >
          <HighlightedTs code={props.snippet} />
        </pre>
      </Box>
      <IconButton
        variant="soft"
        color={RADIX.COLOR.NEUTRAL}
        title="Copy usage snippet"
        style={{
          position: 'absolute',
          top: 'var(--space-2)',
          right: 'var(--space-2)',
        }}
        onClick={() => {
          void navigator.clipboard.writeText(props.snippet);
          NotifyHelper.success({
            message_md: t('common.x-copied-to-clipboard', {
              x: 'Usage snippet',
            }),
          });
        }}
      >
        <CopyIcon />
      </IconButton>
    </Box>
  </Flex>
);

const BackToList = () => {
  const navigate = useNavigate();

  return (
    <Button
      size="2"
      variant="soft"
      color="gray"
      onClick={() => navigate(paths.platformConsole.featureFlags)}
    >
      <ArrowLeftIcon />
      Back to feature flags
    </Button>
  );
};

interface IContentProps {
  mode: 'create' | 'edit';
  /** present in edit mode; undefined when creating */
  flag?: IFeatureFlag;
  environment: FeatureFlagEnvironment;
}

const FeatureFlagDetailContent = (props: IContentProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const currentUserID = useAuthStore((s) => s.current.userID);

  // captured as a const local so TypeScript keeps the narrowing inside the
  // dialog callbacks below (property narrowing would not survive the closures)
  const flag = props.flag;
  const isEdit = props.mode === 'edit';

  const [tab, setTab] = useState<DetailTab>(
    searchParams.get('tab') === 'history' ? 'history' : 'details'
  );

  const [loading, setLoading] = useState(false);
  const [name, setName] = useState(flag?.name ?? '');
  const [description, setDescription] = useState(flag?.description ?? '');
  const [enabled, setEnabled] = useState(flag?.enabled ?? false);
  const [criteria, setCriteria] = useState<IFeatureFlagCriterion[]>(
    flag?.criteria ?? []
  );
  const [incompleteCriteria, setIncompleteCriteria] = useState<string[]>([]);

  const [dialogDuplicate, setDialogDuplicate] = useState<number>();
  const [dialogDelete, setDialogDelete] = useState<number>();
  const [dialogSave, setDialogSave] = useState<number>();

  const handleCriteriaChange = useCallback(
    (next: IFeatureFlagCriteriaState) => {
      setCriteria(next.criteria);
      setIncompleteCriteria(next.incomplete);
    },
    []
  );

  const trimmedName = name.trim();

  /** same fail-on-first-error validation the create/edit dialog used */
  const validate = (): boolean => {
    if (!isEdit && !trimmedName) {
      NotifyHelper.error({
        message_md: 'Please provide a flag name and try again.',
      });
      return false;
    }

    if (incompleteCriteria.length > 0) {
      NotifyHelper.error({
        message_md: `Please select at least one ${incompleteCriteria[0]} for the enabled targeting criteria and try again.`,
      });
      return false;
    }

    return true;
  };

  const performSave = async () => {
    setLoading(true);

    try {
      const service = PlatformConsoleService.getInstance();

      const result = flag
        ? await service.updateFeatureFlag(flag._id, {
            description,
            enabled,
            criteria,
          })
        : await service.createFeatureFlag({
            name: trimmedName,
            description,
            environment: props.environment,
            enabled,
            criteria,
          });

      if (!result?.success) {
        NotifyHelper.error({
          message_md:
            result?.error ??
            `Failed to ${isEdit ? 'update' : 'create'} the feature flag.`,
        });
        return;
      }

      NotifyHelper.success({
        message_md: `Feature flag \`${trimmedName || flag?.name}\` ${
          isEdit ? 'updated' : 'created'
        }.`,
      });

      navigate(paths.platformConsole.featureFlags);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Gate save behind an ownership warning: editing a flag owned by someone else
   * confirms first. Create mode (no owner yet) and owners save directly.
   */
  const handleSave = () => {
    if (loading) {
      return;
    }

    if (!validate()) {
      return;
    }

    if (isEdit && flag && !FeatureFlagHelper.isOwnedBy(flag, currentUserID)) {
      setDialogSave(Date.now());
      return;
    }

    void performSave();
  };

  const detailsForm = (
    <CommonFormGrid columns={1}>
      <CommonTextInput
        id="feature-flag-name"
        label="Name"
        name="name"
        disabled={loading || isEdit}
        value={name}
        onChange={(v) => setName(v ?? '')}
        placeholder="e.g. new-pitch-designer"
        hint_md={
          isEdit
            ? 'The name is fixed once created.'
            : `Unique within the ${props.environment} environment.`
        }
      />

      {name === FF_NAME_CONTINUOUS_TRAINING && (
        <CommonCallout
          color={RADIX.COLOR.WARNING}
          text={`Note: CT should only be enabled for machines using one of the following tracking devices: ${CONTINUOUS_TRAINING_SUPPORTED_DEVICES.join(', ')}.`}
        />
      )}

      <CommonTextInput
        id="feature-flag-description"
        label="Description"
        name="description"
        disabled={loading}
        value={description}
        onChange={(v) => setDescription(v ?? '')}
        optional
      />

      <CommonCheckboxInput
        id="feature-flag-enabled"
        label="Enabled"
        name="enabled"
        disabled={loading}
        checked={enabled}
        onChange={(v) => setEnabled(!!v)}
        hint_md="Master on/off switch for this environment."
      />

      <FeatureFlagCriteriaComposer
        initialCriteria={flag?.criteria ?? []}
        disabled={loading}
        onChange={handleCriteriaChange}
      />
    </CommonFormGrid>
  );

  const backendSnippet = flag ? buildBackendSnippet(flag.name, criteria) : '';
  const frontendSnippet = flag ? buildFrontendSnippet(flag.name) : '';

  const saveBar = (
    <Flex gap="3" justify="end">
      <Button
        variant="soft"
        color="gray"
        disabled={loading}
        onClick={() => navigate(paths.platformConsole.featureFlags)}
      >
        Cancel
      </Button>
      <Button color={RADIX.COLOR.ACCENT} loading={loading} onClick={handleSave}>
        {isEdit ? 'Save' : 'Create'}
      </Button>
    </Flex>
  );

  return (
    <Flex direction="column" gap={RADIX.FLEX.GAP.SECTION}>
      <Flex align="center" justify="between" gap="3" wrap="wrap">
        <BackToList />

        {flag && (
          <Flex align="center" gap="2">
            {CAN_DUPLICATE && (
              <Button
                variant="soft"
                color={RADIX.COLOR.NEUTRAL}
                disabled={loading}
                onClick={() => setDialogDuplicate(Date.now())}
              >
                <CopyIcon />
                Duplicate
              </Button>
            )}
            <Button
              variant="soft"
              color={RADIX.COLOR.DANGER}
              disabled={loading}
              onClick={() => setDialogDelete(Date.now())}
            >
              <TrashIcon />
              Delete
            </Button>
          </Flex>
        )}
      </Flex>

      <Flex direction="column" gap="2" mt="4">
        <Flex align="center" gap="3" wrap="wrap">
          <Heading size="5">
            {flag ? (
              <Code color={RADIX.COLOR.NEUTRAL}>{flag.name}</Code>
            ) : (
              'New Feature Flag'
            )}
          </Heading>
          <Badge color="indigo" variant="soft">
            {environmentLabel(props.environment)}
          </Badge>
          {flag && (
            <Badge
              color={enabled ? RADIX.COLOR.SUCCESS : RADIX.COLOR.NEUTRAL}
              variant="soft"
            >
              {enabled ? 'Enabled' : 'Disabled'}
            </Badge>
          )}
        </Flex>

        {flag && (
          <Flex align="center" gap="2" mt="1">
            <Text size="2" color="gray">
              Owner
            </Text>
            <Badge color={RADIX.COLOR.NEUTRAL} variant="soft">
              {flag.createdBy?.email ?? '—'}
            </Badge>
            {flag.updatedBy?.email &&
              flag.updatedBy.email !== flag.createdBy?.email && (
                <Text size="1" color="gray">
                  Last updated by {flag.updatedBy.email}
                </Text>
              )}
          </Flex>
        )}

        {flag && (
          <Flex direction="column" gap={RADIX.FLEX.GAP.MD} mt="2">
            <Text
              size="1"
              weight="bold"
              style={{
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                color: 'var(--gray-a11)',
              }}
            >
              Usage
            </Text>
            <UsageSnippet
              label="Backend"
              color="indigo"
              snippet={backendSnippet}
            />
            <UsageSnippet
              label="Frontend"
              color="jade"
              snippet={frontendSnippet}
            />
          </Flex>
        )}
      </Flex>

      {flag ? (
        <Tabs.Root value={tab} onValueChange={(v) => setTab(v as DetailTab)}>
          <Tabs.List>
            <Tabs.Trigger value="details">Details</Tabs.Trigger>
            <Tabs.Trigger value="history">History</Tabs.Trigger>
          </Tabs.List>

          <Box pt="4">
            {tab === 'details' && (
              <Flex direction="column" gap={RADIX.FLEX.GAP.SECTION}>
                {detailsForm}
                {saveBar}
              </Flex>
            )}
            {tab === 'history' && (
              <FeatureFlagAuditHistory
                flag={flag}
                environment={props.environment}
              />
            )}
          </Box>
        </Tabs.Root>
      ) : (
        <Flex direction="column" gap={RADIX.FLEX.GAP.SECTION}>
          {detailsForm}
          {saveBar}
        </Flex>
      )}

      {dialogDuplicate && flag && (
        <FeatureFlagDuplicateDialog
          key={dialogDuplicate}
          flag={flag}
          onClose={() => setDialogDuplicate(undefined)}
        />
      )}

      {dialogDelete && flag && (
        <CommonConfirmationDialog
          key={dialogDelete}
          identifier="ConfirmDeleteFeatureFlag"
          maxWidth={RADIX.DIALOG.WIDTH.MD}
          title="Delete Feature Flag"
          content={
            <Flex direction="column" gap="2">
              <Text>
                This will permanently delete <Code>{flag.name}</Code> from the{' '}
                {flag.environment} environment. This cannot be undone.
              </Text>
              {!FeatureFlagHelper.isOwnedBy(flag, currentUserID) && (
                <Text color={RADIX.COLOR.WARNING}>
                  This flag is owned by{' '}
                  <Code>{flag.createdBy?.email ?? 'someone else'}</Code>.
                </Text>
              )}
            </Flex>
          }
          action={{
            label: 'Delete',
            color: RADIX.COLOR.DANGER,
            onClick: async () => {
              const result =
                await PlatformConsoleService.getInstance().deleteFeatureFlag(
                  flag._id
                );

              if (!result?.success) {
                NotifyHelper.error({
                  message_md:
                    result?.error ?? 'Failed to delete the feature flag.',
                });
                return;
              }

              NotifyHelper.success({
                message_md: `Feature flag \`${flag.name}\` deleted.`,
              });

              navigate(paths.platformConsole.featureFlags);
            },
          }}
        />
      )}

      {dialogSave && flag && (
        <CommonConfirmationDialog
          key={dialogSave}
          identifier="ConfirmSaveFeatureFlag"
          maxWidth={RADIX.DIALOG.WIDTH.MD}
          title="Modify Feature Flag"
          content={
            <Text>
              This flag is owned by{' '}
              <Code>{flag.createdBy?.email ?? 'someone else'}</Code>. Are you
              sure you want to save your changes?
            </Text>
          }
          action={{
            label: 'Save',
            color: RADIX.COLOR.WARNING,
            onClick: async () => {
              await performSave();
            },
          }}
          onClose={() => setDialogSave(undefined)}
        />
      )}
    </Flex>
  );
};

/**
 * Standalone create/edit page for a single feature flag. Edit mode loads the
 * flag by id so the page is deep-linkable / refresh-safe; create mode seeds the
 * environment from the `?environment=` query param (falling back to the runtime
 * default). Hosts the Details form and the audit History tab.
 */
export const FeatureFlagDetailPage = (props: IProps) => {
  const { flagId } = useParams();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(props.mode === 'edit');
  const [flag, setFlag] = useState<IFeatureFlag>();
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (props.mode !== 'edit' || !flagId) {
      return;
    }

    let active = true;

    setLoading(true);

    PlatformConsoleService.getInstance()
      .getFeatureFlag(flagId)
      .then((result) => {
        if (!active) {
          return;
        }

        if (result) {
          setFlag(result);
        } else {
          setNotFound(true);
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [props.mode, flagId]);

  const environment = useMemo<FeatureFlagEnvironment>(() => {
    if (props.mode === 'edit') {
      return flag?.environment ?? DEFAULT_ENVIRONMENT;
    }

    const param = searchParams.get('environment');
    const matched = FEATURE_FLAG_ENVIRONMENT_OPTIONS.find(
      (o) =>
        o.value === param &&
        MANAGEABLE_ENVIRONMENTS.includes(o.value as FeatureFlagEnvironment)
    )?.value as FeatureFlagEnvironment | undefined;

    return matched ?? DEFAULT_ENVIRONMENT;
  }, [props.mode, flag, searchParams]);

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      {loading ? (
        <Flex align="center" justify="center" py="6">
          <Spinner />
        </Flex>
      ) : notFound ? (
        <Flex direction="column" gap={RADIX.FLEX.GAP.SECTION}>
          <BackToList />
          <Text color="gray">This feature flag no longer exists.</Text>
        </Flex>
      ) : (
        <FeatureFlagDetailContent
          key={flag?._id ?? 'create'}
          mode={props.mode}
          flag={flag}
          environment={environment}
        />
      )}
    </ErrorBoundary>
  );
};
